import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Dropdown from '../Dropdown';
import { DropdownItem } from '../Dropdown/config';
import PanelHeading from '../PanelHeading';
import Field from '../Field';
import IconButton from '../IconButton';
import { PlusIcon, TrashIcon } from '../IconButton/icons';
import { SaveState } from '../SaveDot';
import './team-panel.scss';

const initialsOf = (fullName: string) =>
  fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

interface TeamPanelProps {
  projectId: number;
  stageId: number | null;
  onChange?: () => void;
}

interface AssignableTeamMember {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  type: string;
}

interface CurrentTeamMember {
  id: string;
  full_name: string;
  type: string;
  is_lead: boolean;
}

interface TeamMemberJoinRow {
  team_member_id: string;
  team_members:
    | { first_name: string; last_name: string; type: string }
    | { first_name: string; last_name: string; type: string }[]
    | null;
}

const TeamPanel: React.FC<TeamPanelProps> = ({ projectId, stageId, onChange }) => {
  const supabase = useSupabaseClient();

  const [assignable, setAssignable] = useState<AssignableTeamMember[]>([]);
  const [currentTeam, setCurrentTeam] = useState<CurrentTeamMember[]>([]);
  const [leadId, setLeadId] = useState<string>('');
  const [selectedToAdd, setSelectedToAdd] = useState<string>('');
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isSavingLead, setIsSavingLead] = useState(false);
  /** The lead field's own save dot; `saved` clears itself. */
  const [leadSave, setLeadSave] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (leadSave !== 'saved') return;
    const t = setTimeout(() => setLeadSave('idle'), 1500);
    return () => clearTimeout(t);
  }, [leadSave]);

  const fetchAssignable = useCallback(async () => {
    const { data, error: rpcError } = await supabase
      .rpc('search_assignable_team_members', { search_term: null, limit_results: 100 });
    if (rpcError) {
      console.error('Error fetching assignable team members:', rpcError);
      return;
    }
    setAssignable((data || []) as AssignableTeamMember[]);
  }, [supabase]);

  const fetchTeamAndLead = useCallback(async () => {
    if (stageId) {
      const [{ data: members, error: membersError }, { data: stage, error: stageError }] =
        await Promise.all([
          supabase
            .from('project_stage_team_members')
            .select('team_member_id, team_members(id, first_name, last_name, type)')
            .eq('stage_id', stageId),
          supabase
            .from('project_stages')
            .select('project_lead')
            .eq('id', stageId)
            .single(),
        ]);

      if (membersError) {
        console.error('Error fetching stage team members:', membersError);
        setError(membersError.message);
        return;
      }
      if (stageError) {
        console.error('Error fetching stage lead:', stageError);
      }

      const lead = stage?.project_lead ?? '';
      setLeadId(lead || '');

      const rows = ((members ?? []) as TeamMemberJoinRow[]).map((row) => {
        const tm = Array.isArray(row.team_members) ? row.team_members[0] : row.team_members;
        return {
          id: row.team_member_id,
          full_name: tm ? `${tm.first_name} ${tm.last_name}` : '—',
          type: tm?.type ?? '',
          is_lead: row.team_member_id === lead,
        };
      });
      setCurrentTeam(rows);
    } else {
      const [{ data: members, error: membersError }, { data: project, error: projectError }] =
        await Promise.all([
          supabase
            .from('project_team_members')
            .select('team_member_id, team_members(id, first_name, last_name, type)')
            .eq('project_id', projectId),
          supabase
            .from('projects')
            .select('project_lead')
            .eq('id', projectId)
            .single(),
        ]);

      if (membersError) {
        console.error('Error fetching project team members:', membersError);
        setError(membersError.message);
        return;
      }
      if (projectError) {
        console.error('Error fetching project lead:', projectError);
      }

      const lead = project?.project_lead ?? '';
      setLeadId(lead || '');

      const rows = ((members ?? []) as TeamMemberJoinRow[]).map((row) => {
        const tm = Array.isArray(row.team_members) ? row.team_members[0] : row.team_members;
        return {
          id: row.team_member_id,
          full_name: tm ? `${tm.first_name} ${tm.last_name}` : '—',
          type: tm?.type ?? '',
          is_lead: row.team_member_id === lead,
        };
      });
      setCurrentTeam(rows);
    }
  }, [supabase, projectId, stageId]);

  useEffect(() => {
    fetchAssignable();
  }, [fetchAssignable]);

  useEffect(() => {
    fetchTeamAndLead();
  }, [fetchTeamAndLead]);

  const handleAdd = useCallback(async () => {
    if (!selectedToAdd) return;
    setIsAdding(true);
    setError(null);
    try {
      const rpcName = stageId ? 'add_stage_team_member' : 'add_project_team_member';
      const rpcParams = stageId
        ? { p_stage_id: stageId, p_team_member_id: selectedToAdd }
        : { p_project_id: projectId, p_team_member_id: selectedToAdd };

      const { data, error: rpcError } = await supabase.rpc(rpcName, rpcParams);
      if (rpcError) throw new Error(rpcError.message);
      if (data && !data.success) throw new Error(data.error || 'Failed to add team member');

      setSelectedToAdd('');
      await fetchTeamAndLead();
      onChange?.();
    } catch (err) {
      console.error('Error adding team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to add team member');
    } finally {
      setIsAdding(false);
    }
  }, [selectedToAdd, stageId, projectId, supabase, fetchTeamAndLead, onChange]);

  const handleRemove = useCallback(async (teamMemberId: string) => {
    setRemovingId(teamMemberId);
    setError(null);
    try {
      const rpcName = stageId ? 'remove_stage_team_member' : 'remove_project_team_member';
      const rpcParams = stageId
        ? { p_stage_id: stageId, p_team_member_id: teamMemberId }
        : { p_project_id: projectId, p_team_member_id: teamMemberId };

      const { data, error: rpcError } = await supabase.rpc(rpcName, rpcParams);
      if (rpcError) throw new Error(rpcError.message);
      if (data && !data.success) throw new Error(data.error || 'Failed to remove team member');

      await fetchTeamAndLead();
      onChange?.();
    } catch (err) {
      console.error('Error removing team member:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove team member');
    } finally {
      setRemovingId(null);
    }
  }, [stageId, projectId, supabase, fetchTeamAndLead, onChange]);

  const handleUpdateLead = useCallback(async (newLeadId: string) => {
    const previous = leadId;
    setLeadId(newLeadId);
    setIsSavingLead(true);
    setLeadSave('saving');
    setError(null);
    try {
      if (stageId) {
        const { data, error: rpcError } = await supabase.rpc('update_project_stage', {
          p_stage_id: stageId,
          p_stage_data: { project_lead: newLeadId || null },
        });
        if (rpcError) throw new Error(rpcError.message);
        if (data && !data.success) throw new Error(data.error || 'Failed to update stage lead');
      } else {
        const { data, error: rpcError } = await supabase.rpc('update_project_detail', {
          project_id: projectId,
          project_data: { project_lead: newLeadId || null },
        });
        if (rpcError) throw new Error(rpcError.message);
        if (data && !data.success) throw new Error(data.error || 'Failed to update project lead');
      }
      await fetchTeamAndLead();
      setLeadSave('saved');
      onChange?.();
    } catch (err) {
      console.error('Error updating lead:', err);
      setError(err instanceof Error ? err.message : 'Failed to update lead');
      setLeadId(previous);
      setLeadSave('error');
    } finally {
      setIsSavingLead(false);
    }
  }, [leadId, stageId, projectId, supabase, fetchTeamAndLead, onChange]);

  const leadDropdownItems = useMemo<DropdownItem[]>(() => {
    const base: DropdownItem[] = stageId
      ? [{ id: '', label: 'Inherit from parent project', value: '' }]
      : [{ id: '', label: 'None', value: '' }];
    return [
      ...base,
      ...assignable.map((tm) => ({
        id: tm.id,
        label: tm.full_name,
        value: tm.id,
      })),
    ];
  }, [assignable, stageId]);

  const availableToAdd = useMemo<DropdownItem[]>(() => {
    const existing = new Set(currentTeam.map((m) => m.id));
    return assignable
      .filter((tm) => !existing.has(tm.id))
      .map((tm) => ({
        id: tm.id,
        label: `${tm.full_name} (${tm.type})`,
        value: tm.id,
      }));
  }, [assignable, currentTeam]);

  const noun = stageId ? 'stage' : 'project';

  return (
    <div className="team-panel form-ui">
      <PanelHeading title="Lead" />
      <div className="pdm-grid pdm-grid--2">
        <Field
          label={stageId ? 'Stage lead' : 'Project lead'}
          saveState={leadSave}
          hint={stageId
            ? 'Leave as inherited to use the project lead. The lead is always on the team.'
            : 'The lead is always on the team.'}
        >
          <Dropdown
            items={leadDropdownItems}
            value={leadId}
            onChange={handleUpdateLead}
            placeholder={stageId ? 'Select stage lead...' : 'Select project lead...'}
            searchable
            disabled={isSavingLead}
          />
        </Field>
      </div>

      <PanelHeading
        spaced
        title="Team members"
        meta={`${currentTeam.length}`}
        hint={`People on the team can filter the workflow boards to the ${noun}s they are working on.`}
      />

      {error && <p className="field__error" role="alert">{error}</p>}

      <div className="team-panel__add">
        <Dropdown
          items={availableToAdd}
          value={selectedToAdd}
          onChange={setSelectedToAdd}
          placeholder="Add someone to the team…"
          searchable
          className="team-panel__add-select"
        />
        <button
          type="button"
          className="pill-btn pill-btn--primary pill-btn--sm"
          onClick={handleAdd}
          disabled={!selectedToAdd || isAdding}
        >
          <PlusIcon size={12} /> {isAdding ? 'Adding…' : 'Add'}
        </button>
      </div>

      {currentTeam.length === 0 ? (
        <p className="ui-table__empty">No team members on this {noun} yet.</p>
      ) : (
        <div className="ui-cards">
          {currentTeam.map((member) => {
            const busy = removingId === member.id;
            return (
              <div key={member.id} className={`ui-card ${member.is_lead ? 'ui-card--static' : ''}`.trim()}>
                <span className="ui-card__avatar" aria-hidden="true">{initialsOf(member.full_name)}</span>
                <div className="ui-card__main">
                  <div className="ui-card__top">
                    <span className="ui-card__title">{member.full_name}</span>
                  </div>
                  <div className="ui-card__meta">
                    <span>{member.type || <span className="is-empty">No type</span>}</span>
                  </div>
                </div>
                <div className="ui-card__side">
                  {member.is_lead && (
                    <span className="ui-chip ui-chip--lead">{stageId ? 'Stage lead' : 'Project lead'}</span>
                  )}
                  {!member.is_lead && (
                    <span className="row-actions">
                      <IconButton
                        size="sm"
                        tone="danger"
                        icon={<TrashIcon />}
                        label={`Remove ${member.full_name}`}
                        onClick={() => handleRemove(member.id)}
                        disabled={busy}
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamPanel;
